/**
 * The history rail of the AI Buddy module (board P, drawn as the P5 boards' quiet rail, owner,
 * 4 November 2026): „Rozmowy", „Nowa rozmowa" as a plain row, and the trainer's conversations
 * grouped by age — a one-line title with its time, and under it, muted, who it is for and what it
 * waits for.
 *
 * ── It keeps its box in every state ──────────────────────────────────────────────────────────
 * The column is 272px wide and as tall as the page, so nothing beside it moves whatever it holds.
 * Its list has three states besides the list itself, each drawn inside that box: waiting (rows of
 * the list's own shape, behind the skeleton gate), failed (a sentence and a retry) and empty (a
 * drawing and a positive sentence). The head and „Nowa rozmowa" stand from the first frame.
 *
 * ── The names arrive with the rows ───────────────────────────────────────────────────────────
 * A row names its recipient, and the name is read on the page from the client's id. The page hands
 * the list over only once both reads have answered (`state`), so a row never stands for a moment
 * as „Biblioteka" before its client's name replaces it.
 *
 * ── Paging ───────────────────────────────────────────────────────────────────────────────────
 * The next page is asked for when the list's end scrolls into view; a button at the end is the
 * same act for a keyboard, and is what stands there while a page is on its way.
 */

import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AiHistoryEntry, AiHistoryGroup } from '~/entities/ai-conversation';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { momentWords, waitsForWords } from '../lib/historyWording';

import { PlusGlyph } from './ChatGlyphs';
import styles from './HistoryRail.module.css';

export type HistoryRailState =
  | { readonly kind: 'pending' }
  | { readonly kind: 'failed'; readonly onRetry: () => void }
  | {
      readonly kind: 'ready';
      readonly groups: readonly AiHistoryGroup[];
      /** How many conversations there are, as the server counts them; `null` when it did not say. */
      readonly total: number | null;
      /** More pages exist; `onMore` asks for the next one. */
      readonly hasMore: boolean;
      readonly loadingMore: boolean;
      readonly onMore: () => void;
    };

export interface HistoryRailProps {
  readonly state: HistoryRailState;
  /** The client's name for a row built for one; `null` when the client is not on the roster any more. */
  readonly clientName: (clientId: number) => string | null;
  /** The conversation open beside the rail, or `null` on a new one. */
  readonly openConversationId: number | null;
  /** „Nowa rozmowa"; `null` while Buddy cannot start one, with `newRefusal` saying why. */
  readonly onNew: (() => void) | null;
  /**
   * Why Buddy cannot start a conversation now (switched off, past the day's ceiling), or `null`.
   * Said on „Nowa rozmowa", and in place of the empty list's drawing: an empty history then is not
   * the good news the drawing tells.
   */
  readonly newRefusal: string | null;
  readonly now: Date;
  readonly timeZone: string;
}

/** How many rows the wait draws: enough to read as a list, short of filling the column. */
const SKELETON_ROWS = 4;

export function HistoryRail({ state, clientName, openConversationId, onNew, newRefusal, now, timeZone }: HistoryRailProps) {
  const { t, i18n } = useTranslation();
  const gate = useSkeletonGate(state.kind === 'pending');
  // The list's end, as a node held in state: the list may first mount after a slow first read, and
  // the watch below has to arm then, not only on the rail's own mount.
  const [end, setEnd] = useState<HTMLLIElement | null>(null);
  const hasMore = state.kind === 'ready' && state.hasMore;
  const onMore = state.kind === 'ready' ? state.onMore : null;

  // A press whose button goes with it (a retry, the last „Pokaż starsze") hands focus to the list,
  // so it never falls to the page.
  const list = useRef<HTMLDivElement>(null);
  const focusListWhenGone = useRef<'retry' | 'more' | null>(null);
  const retryShown = state.kind === 'failed' && !gate.waiting;
  const loadingMore = state.kind === 'ready' && state.loadingMore;
  useEffect(() => {
    const asked = focusListWhenGone.current;
    if ((asked === 'retry' && !retryShown) || (asked === 'more' && !hasMore)) {
      focusListWhenGone.current = null;
      list.current?.focus();
    } else if (asked === 'more' && !loadingMore) {
      // The page landed and there are more: the button stayed, and so did focus.
      focusListWhenGone.current = null;
    }
  }, [retryShown, hasMore, loadingMore]);

  // The next page, when the list's end comes into view.
  useEffect(() => {
    const node = end;
    if (node === null || !hasMore || onMore === null) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onMore();
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [end, hasMore, onMore]);

  const row = (entry: AiHistoryEntry) => {
    const recipient = entry.clientId === null ? null : clientName(entry.clientId);
    const waits = waitsForWords(t, entry);
    const open = entry.id === openConversationId;
    return (
      <li key={entry.id}>
        <Link
          to="/buddy"
          search={{ rozmowa: entry.id }}
          className={clsx(styles.row, open && styles.rowOpen)}
          aria-current={open ? 'page' : undefined}
        >
          {/* Read in the order a trainer says it — title, who, when, what it waits for — and drawn as
            * two lines: the title with its time, and under it the muted rest (the grid places them). */}
          <span className={styles.rowTitle} title={entry.title ?? undefined}>
            {entry.title ?? t('web:aiPlan.history.untitled')}
          </span>
          <span className={styles.rowRecipient}>
            {entry.clientId === null ? t('web:aiPlan.history.library') : (recipient ?? t('web:aiPlan.formerClient'))}
          </span>
          <span className={styles.rowTime}>{momentWords(entry.lastMessageAt, now, timeZone, i18n.language)}</span>
          {waits !== null && (
            <>
              <span className={styles.rowSeparator} aria-hidden="true">
                {t('web:aiPlan.history.separator')}
              </span>
              <span className={styles.rowWaits}>{waits}</span>
            </>
          )}
        </Link>
      </li>
    );
  };

  let content;
  if (gate.waiting) {
    content = (
      <div ref={list} tabIndex={-1} className={styles.list} aria-busy="true">
        <span className={hidden.srOnly}>{t('web:aiPlan.history.loading')}</span>
        {gate.skeleton && (
          <span className={styles.group} aria-hidden="true">
            <span className={styles.skeletonHead}>
              <i className={clsx(skel.bar, styles.skeletonHeadBar)} />
            </span>
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <span key={index} className={styles.skeletonRow}>
                <span className={styles.skeletonLine}>
                  <i className={clsx(skel.bar, styles.skeletonTitle)} />
                </span>
                <span className={styles.skeletonSub}>
                  <i className={clsx(skel.bar, styles.skeletonMeta)} />
                </span>
              </span>
            ))}
          </span>
        )}
      </div>
    );
  } else if (state.kind === 'failed') {
    const { onRetry } = state;
    content = (
      <div ref={list} tabIndex={-1} className={clsx(styles.list, styles.note, gate.reveal)}>
        <p className={styles.noteText} role="alert">
          {t('web:aiPlan.history.failed')}
        </p>
        <button
          type="button"
          className={styles.noteAction}
          onClick={() => {
            focusListWhenGone.current = 'retry';
            onRetry();
          }}
        >
          {t('web:aiPlan.history.retry')}
        </button>
      </div>
    );
  } else if (state.kind === 'ready' && state.groups.length === 0) {
    content = (
      <div ref={list} tabIndex={-1} className={clsx(styles.list, styles.note, gate.reveal)}>
        {newRefusal === null ? (
          <EmptyState
            mark={<InboxMarkIcon />}
            tone="positive"
            title={t('web:aiPlan.history.empty.title')}
            hint={t('web:aiPlan.history.empty.hint')}
          />
        ) : (
          <p className={styles.noteText}>{newRefusal}</p>
        )}
      </div>
    );
  } else if (state.kind === 'ready') {
    content = (
      <div
        ref={list}
        className={clsx(styles.list, styles.scroller, gate.reveal)}
        tabIndex={0}
        role="group"
        aria-label={t('web:aiPlan.history.listLabel')}
      >
        {state.groups.map((group) => (
          <section key={group.key} className={styles.group} aria-label={t(`web:aiPlan.history.group.${group.key}`)}>
            <h3 className={styles.groupTitle}>{t(`web:aiPlan.history.group.${group.key}`)}</h3>
            <ul className={styles.rows}>{group.entries.map(row)}</ul>
          </section>
        ))}
        {state.hasMore && (
          <ul className={styles.rows}>
            <li ref={setEnd}>
              <button
                type="button"
                className={styles.more}
                aria-disabled={state.loadingMore ? 'true' : undefined}
                onClick={() => {
                  if (state.loadingMore) return;
                  focusListWhenGone.current = 'more';
                  state.onMore();
                }}
              >
                {state.loadingMore ? t('web:aiPlan.history.loadingMore') : t('web:aiPlan.history.more')}
              </button>
            </li>
          </ul>
        )}
      </div>
    );
  }

  return (
    <aside className={styles.rail} aria-label={t('web:aiPlan.history.title')}>
      <div className={styles.head}>
        <h2 className={styles.title}>{t('web:aiPlan.history.title')}</h2>
        {state.kind === 'ready' && state.total !== null && state.total > 0 && (
          <span className={styles.count}>
            <span aria-hidden="true">{state.total}</span>
            <span className={hidden.srOnly}>{t('web:aiPlan.history.count', { count: state.total })}</span>
          </span>
        )}
      </div>
      <button
        type="button"
        className={styles.newConversation}
        aria-disabled={onNew === null ? 'true' : undefined}
        title={newRefusal ?? undefined}
        aria-label={newRefusal === null ? undefined : `${t('web:aiPlan.history.new')}, ${newRefusal}`}
        onClick={() => {
          onNew?.();
        }}
      >
        <PlusGlyph className={styles.newGlyph} />
        {t('web:aiPlan.history.new')}
      </button>
      {content}
      <p className={styles.foot}>{t('web:aiPlan.history.retention')}</p>
    </aside>
  );
}
